import DocumentSource from './DocumentSource';
import AdvancedButtons from './AdvancedButtons';
import BuildIcon from '@material-ui/icons/Build';
import DatatypeDialog from './DatatypeDialog';
import TypedLiteralDialog from '../core/TypedLiteralDialog';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Notification from '../core/Notification';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import DatatypeDisplay from '../core/DatatypeDisplay';
import FindResourceDialog from './FindResourceDialog';
import GS from '../grafsync/grafsync';
import { v4 as uuidv4 } from 'uuid';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import Errored from '../core/Errored';
import SecondaryButton from '../core/SecondaryButton';
import { BN_NAMESPACE } from '../core/utils/constants';
import { globalStyles as styles } from '../core/style/gstheme';
import LiteralConfirmationField from '../core/LiteralConfirmationField';
import withGS from '../core/withGShoc';
import BlankNodeHandler from './BlankNodeHandler';
import { makeCancelable, getDatatypeMenuOptions, computeRange, deleteBlankNode, isSupportedDatatype } from '../core/utils/utils';
import GetBooleanValueDialog from '../core/GetBooleanValueDialog';

const NNI = "http://www.w3.org/2001/XMLSchema#nonNegativeInteger";
// We also define SHACL shapes for these properties to be functional so that if two users add the same prop at the same time
// one of thos additions will be rejected
const propsList = ["someValuesFrom", "allValuesFrom", "hasValue", "hasSelf",
  "cardinality", "minCardinality", "maxCardinality", "qualifiedCardinality",
  "minQualifiedCardinality", "maxQualifiedCardinality"];

class RestrictionHandler extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isErrored: undefined,
      isLoading: true,
      isObjectProperty: "onClass" in props.data ?
        true
        :
        undefined,
      isTransitiveProperty: undefined,
      chooseCardinality: false,
      chooseQualifiedCardinality: false,
      range: undefined,
      onClass: undefined,
      onDataRange: undefined,
      constraintType: undefined,
      menuOptions: getDatatypeMenuOptions()
    };
    this.cancellable = null;
  }

  // If a property is added that conflicts with any constraint we need to kill dialogs
  static getDerivedStateFromProps(props, state) {
    const dataProps = Object.keys(props.data).filter(k => propsList.includes(k) && props.data[k] !== undefined);
    if (state.constraintType && dataProps.length > 0) {
      return {
        constraintType: undefined
      };
    }
    return null;
  }

  componentDidMount() {
    if ("onProperty" in this.props.data) {
      this.getPropertyData(this.props.data.onProperty, this.props.graph);
    }
    else {
      this.setState({ isLoading: false });
    }
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  loadData(iri) {
    this.cancellable = makeCancelable(GS.loadData([iri], {}, this.props.graph, false));
    return this.cancellable;
  }

  onChooseCardinality = () => this.setState({ chooseCardinality: true });

  onChooseQualifiedCardinality = () => this.setState({ chooseQualifiedCardinality: true });

  addProperty = (property) => {
    this.setState({ loadingProperty: true }, () => {
      GS.addProperty(this.props.id, this.props.graph, "onProperty", property);
      this.getPropertyData(property, this.props.graph);
    });
  };

  addCardinalityProp = (cardinalityPropName) => {
    GS.addProperty(this.props.id, this.props.graph, cardinalityPropName, "1", null, NNI);
    this.setState({ chooseCardinality: false, chooseQualifiedCardinality: false });
    if (this.state.range) {
      const computedRange = computeRange(this.state.range);
      if (computedRange) this.addOnDataRange(computedRange);
    }
  };

  getPropertyData = async (property, graph) => {
    try {
      await this.loadData(property, graph).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      GS.handle("restrictions");
      const propertyData = GS.data(property, graph, false);
      let isObjectProperty = this.state.isObjectProperty;
      let isTransitiveProperty = this.state.isTransitiveProperty;
      let range = this.state.range;
      if (GS.isLoaded(property, graph)) {
        const propTypes = Array.isArray(propertyData.type) ? propertyData.type : [propertyData.type];
        isObjectProperty = propTypes.includes("ObjectProperty");
        isTransitiveProperty = propTypes.includes("TransitiveProperty");
        range = propertyData.range ?
          Array.isArray(propertyData.range) ?
            [...propertyData.range]
            :
            [propertyData.range]
          :
          undefined;
      }
      this.setState({
        isLoading: false,
        loadingProperty: false,
        isObjectProperty,
        isTransitiveProperty,
        range
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  setConstraintType = (constraintType) => this.setState({ constraintType });

  addHasValueIRI = (iri) => {
    GS.addProperty(this.props.id, this.props.graph, "hasValue", iri, null, null, this.props.graph, "@id");
  };

  addOnClass = (iri) => {
    GS.addProperty(this.props.id, this.props.graph, "onClass", iri, null, null, this.props.graph);
  };

  // Used for both object and datatype property classes
  addSomeValuesFromClass = (iri) => {
    GS.addProperty(this.props.id, this.props.graph, "someValuesFrom", iri, null, null, this.props.graph);
    this.setConstraintType(undefined);
  };

  addAllaluesFromClass = (iri) => {
    GS.addProperty(this.props.id, this.props.graph, "allValuesFrom", iri, null, null, this.props.graph);
    this.setConstraintType(undefined);
  };

  addOnDataRange = (iri) => GS.addProperty(this.props.id, this.props.graph, "onDataRange", iri, null, null, this.props.graph);

  addNewRestriction = (property) => async () => {
    const { graph } = this.props;
    const restrictionIRI = BN_NAMESPACE + uuidv4();
    let newRestriction = {
      id: restrictionIRI,
      type: "Restriction",
      invisibility: this.props.sourceComponent
    };
    await GS.insertResource(newRestriction, restrictionIRI, graph, [this.props.id]);
    GS.addProperty(this.props.id, graph, property, restrictionIRI);
  };


  addValuesFrom = (property) => async () => {
    const { graph } = this.props;
    const classIRI = BN_NAMESPACE + uuidv4();
    let newClass = {
      id: classIRI,
      invisibility: this.props.sourceComponent,
      type: "OwlClass"
    };
    await GS.insertResource(newClass, classIRI, graph, [this.props.id]);
    GS.addProperty(this.props.id, graph, property, classIRI);
  };

  addDatatype = async (property) => {
    const { graph } = this.props;
    const datatypeIRI = BN_NAMESPACE + uuidv4();
    let newDatatype = {
      id: datatypeIRI,
      invisibility: this.props.sourceComponent,
      type: "RdfsDatatype"
    };
    await GS.insertResource(newDatatype, datatypeIRI, graph, [this.props.id]);
    GS.addProperty(this.props.id, graph, property, datatypeIRI);
  };

  addNewTypedLiteral = (value, datatype = null) => {
    GS.addProperty(this.props.id, this.props.graph, "hasValue", value, null, datatype);
    this.setState({ constraintType: undefined });
  };

  addNewLiteral = (language, text) => GS.addProperty(this.props.id, this.props.graph, "hasValue", text, language, null);

  exitConstraint = () => this.setState({ constraintType: undefined });

  addHasSelf = () => GS.addProperty(this.props.id, this.props.graph, "hasSelf", "true", null, "http://www.w3.org/2001/XMLSchema#boolean");

  onUpdate = (property) => (value) => this.props.onUpdate(property, value);

  onDelete = (property, lang, datatype) => (value) => this.props.onDelete(property, value, lang, datatype);

  deleteRestriction = () => deleteBlankNode(this.props.id, this.props.graph);

  render() {

    const { data, id, graph, components, viewLang, defaultLang, top, useWorkflow,
      projectIRI, context, sourceComponent, sourceResource, classes } = this.props;
    const { isErrored, chooseCardinality, chooseQualifiedCardinality, range, loadingProperty,
      isObjectProperty, isLoading, constraintType, isTransitiveProperty } = this.state;

    if (isErrored) return <Errored dataTest="rh-error" data={isErrored} />;
    if (isLoading) return null;

    const isReadOnly = this.props.isReadOnly || data.pendingDeletion;
    const { onProperty, someValuesFrom, allValuesFrom, hasValue, onClass, onDataRange, hasSelf } = data;

    let label = undefined;
    let cardinalityPropName = undefined;
    let isQualifiedCardinality = false;

    const conflictingProps = Object.keys(data).filter(k => propsList.includes(k) && data[k] !== undefined);
    if (conflictingProps.length > 1) {
      return (
        <div data-test="restriction-container" className={classes.incompleteData}>
          {!isReadOnly &&
            <Tooltip title='Delete restriction'>
              <span style={{ float: 'right', marginBottom: 6 }}>
                <Typography variant="body2">
                  <IconButton data-test="delete-restriction" size="small" onClick={this.deleteRestriction} className={classes.deleteButton}>
                    <DeleteIcon />
                  </IconButton>
                </Typography>
              </span>
            </Tooltip>
          }
          <div style={{ clear: 'both' }}>
            <Notification text={"This restriction contains conflicting properties: " + conflictingProps.join(", ") + ". Please delete."} dataTest="conflicting" />
          </div>
        </div>
      );
    }

    if ("someValuesFrom" in data && data.someValuesFrom !== undefined) {
      label = "Some Values From";
    }
    else if ("allValuesFrom" in data && data.allValuesFrom !== undefined) {
      label = "All Values From";
    }
    else if ("hasValue" in data && data.hasValue !== undefined) {
      label = "Has Value";
    }
    else if ("hasSelf" in data && data.hasSelf !== undefined) {
      label = "Has Self";
    }
    else if ("cardinality" in data) {
      label = "Cardinality";
      cardinalityPropName = "cardinality";
    }
    else if ("minCardinality" in data) {
      label = "Minimum Cardinality";
      cardinalityPropName = "minCardinality";
    }
    else if ("maxCardinality" in data) {
      label = "Maximum Cardinality";
      cardinalityPropName = "maxCardinality";
    }
    else if ("qualifiedCardinality" in data) {
      label = "Cardinality (Qualified)";
      cardinalityPropName = "qualifiedCardinality";
      isQualifiedCardinality = true;
    }
    else if ("minQualifiedCardinality" in data) {
      label = "Minimum Cardinality (Qualified)";
      cardinalityPropName = "minQualifiedCardinality";
      isQualifiedCardinality = true;
    }
    else if ("maxQualifiedCardinality" in data) {
      label = "Maximum Cardinality (Qualified)";
      cardinalityPropName = "maxQualifiedCardinality";
      isQualifiedCardinality = true;
    }
    else if (constraintType === "hasValue") {
      label = "Add a Value";
    }

    // onProperty resource may have been deleted causing an incomplete restriction
    const invalid = label && !("onProperty" in data);
    if (invalid) {
      return <div
        data-test="restriction-container"
        data-complete={false}
        className={classes.incompleteData}
      >
        {!isReadOnly &&
          <Tooltip title='Delete restriction'>
            <span style={{ float: 'right', marginBottom: 6 }}>
              <Typography variant="body2">
                <IconButton data-test="delete-restriction" size="small" onClick={this.deleteRestriction} className={classes.deleteButton}>
                  <DeleteIcon />
                </IconButton>
              </Typography>
            </span>
          </Tooltip>
        }
        <div data-test="restriction-content" style={{ padding: 12, clear: 'both' }}>
          {data.pendingDeletion ?
            <Notification error={true} text="This restriction is pending deletion." dataTest="pending-deletion" />
            :
            <Notification error={true} dataTest="invalid-warning" text="The property on which this restriction is based has been deleted causing this restriction to be incomplete. Please delete." />
          }
        </div>
      </div>;
    }

    const from = someValuesFrom || allValuesFrom || hasValue;

    const valueText = cardinalityPropName ?
      data[cardinalityPropName]["@value"] || data[cardinalityPropName]
      :
      undefined;
    const datatype = cardinalityPropName ?
      data[cardinalityPropName].type
      :
      undefined;

    const isComplete = isQualifiedCardinality ?
      "onClass" in data ?
        data.onClass !== undefined
        :
        "onDataRange" in data && data.onDataRange !== undefined
      :
      "onProperty" in data ?
        ("someValuesFrom" in data && data.someValuesFrom !== undefined)
        || ("allValuesFrom" in data && data.allValuesFrom !== undefined)
        || ("hasValue" in data && data.hasValue !== undefined)
        || "cardinality" in data
        || "minCardinality" in data
        || "maxCardinality" in data
        || "hasSelf" in data
        :
        label;

    const computedRange = computeRange(range);
    const isDatatypeSupported = isSupportedDatatype(computedRange);

    if (data.pendingDeletion) return null;

    return (
      <ProjectConsumer>
        {({ projectLang }) => (
          <div
            data-test="restriction-container"
            data-complete={isComplete}
            className={isComplete ?
              top ?
                classes.completeDataDarkTop
                :
                classes.completeDataDark
              :
              classes.incompleteData}
          >
            {data.pendingDeletion &&
              <Notification error={true} text="This restriction is pending deletion." dataTest="pending-deletion" />
            }
            {isReadOnly && !isComplete && !data.pendingDeletion &&
              <Notification text="This contains partially completed structures." dataTest="partial" />
            }
            {!isReadOnly &&
              <Tooltip title='Delete restriction'>
                <span style={{ float: 'right', marginBottom: 6 }}>
                  <Typography variant="body2">
                    <IconButton data-test="delete-restriction" size="small" onClick={this.deleteRestriction} className={classes.deleteButton}>
                      <DeleteIcon />
                    </IconButton>
                  </Typography>
                </span>
              </Tooltip>
            }
            <div data-test="restriction-content">
              {top &&
                <div style={{ width: 'calc(100% - 60px)', marginBottom: 12 }}>
                  <DocumentSource
                    graph={graph}
                    componentIRI={sourceComponent}
                    viewLang={viewLang}
                    defaultLang={defaultLang}
                    sources={data.documentSource} />
                </div>
              }
              {!isReadOnly && !onProperty && !loadingProperty &&
                <div>
                  <Typography paragraph variant="caption" className={classes.instruction}>Choose property to constrain on</Typography>
                  <FindResourceDialog
                    key={viewLang}
                    useButton={true}
                    permitExternal={true}
                    components={components}
                    exclude={[sourceResource]}
                    hideButtonOnOpen={true}
                    defaultLang={defaultLang}
                    permittedExternalType={[
                      "ObjectProperty",
                      "DatatypeProperty"
                    ]}
                    sourceComponent={sourceComponent}
                    searchClass={[
                      "http://www.w3.org/2002/07/owl#ObjectProperty",
                      "http://www.w3.org/2002/07/owl#DatatypeProperty"
                    ]}
                    buttonTitle="Find property"
                    title="Find Property"
                    project={projectIRI}
                    graph={graph}
                    viewLang={viewLang}
                    onAddResource={this.addProperty} />
                </div>
              }
              {onProperty && !loadingProperty &&
                <div data-test={"onproperty-container-" + onProperty}>
                  <div data-test="onproperty-label" style={{ marginBottom: 6 }}>
                    <Typography variant="caption" style={{ color: 'black' }}>On {isObjectProperty ? "Object " : ""}Property</Typography>
                  </div>
                  <div data-test="onproperty-expression">
                    {onProperty.startsWith(BN_NAMESPACE) ?
                      <BlankNodeHandler
                        key={onProperty}
                        id={onProperty}
                        graph={graph}
                        context={context}
                        useWorkflow={useWorkflow}
                        currentRange={computedRange}
                        handleNotAvailable={true}
                        projectIRI={projectIRI}
                        sourceResource={sourceResource}
                        sourceComponent={sourceComponent}
                        components={components}
                        isReadOnly={true}
                        viewLang={viewLang}
                        defaultLang={defaultLang} />
                      :
                      <CommonIriDisplay
                        id={onProperty}
                        components={components}
                        projectIRI={projectIRI}
                        context={context}
                        contextProperty="isDefinedBy"
                        handleNotAvailable={true}
                        property="onProperty"
                        isReadOnly={true}
                        compact={true}
                        graph={graph}
                        source={id}
                        wrap={false}
                        viewLang={viewLang}
                        defaultLang={defaultLang} />
                    }
                  </div>
                  {!isObjectProperty && !onProperty.startsWith(BN_NAMESPACE) && computedRange !== undefined && !isDatatypeSupported &&
                    <Notification text={"The range on this property is not supported: " + computedRange} dataTest="unsupported-datatype" />
                  }
                  {!isReadOnly &&
                    <div data-test="constraint-label" style={{ marginTop: 6 }}>
                      {chooseCardinality ?
                        <Typography paragraph variant="caption" className={classes.instruction}>Choose cardinality constraint</Typography>
                        :
                        chooseQualifiedCardinality ?
                          <Typography paragraph variant="caption" className={classes.instruction}>Choose qualified cardinality constraint</Typography>
                          :
                          label === undefined ?
                            <Typography paragraph variant="caption" className={classes.instruction}>Choose constraint</Typography>
                            :
                            <Typography variant="caption" style={{ color: 'black', marginBottom: 6 }}>{label}</Typography>
                      }
                    </div>
                  }
                  {isReadOnly && label &&
                    <div data-test="constraint-label" style={{ marginTop: 6 }}>
                      <Typography variant="caption" style={{ color: 'black', marginBottom: 6 }}>{label}</Typography>
                    </div>
                  }
                  {
                    cardinalityPropName ?
                      <div>
                        {datatype !== NNI &&
                          <Notification text="The datatype for a cardinality should be a non-negative integer." />
                        }
                        <LiteralConfirmationField
                          dataTest="cardinality"
                          graph={graph}
                          source={id}
                          property={cardinalityPropName}
                          margin="dense"
                          invalidDatatype={datatype !== NNI}
                          text={valueText.toString()}
                          onUpdate={this.onUpdate(cardinalityPropName)}
                          datatype={datatype}
                          canBeDeleted={false}
                          isReadOnly={isReadOnly}
                        />
                        {isQualifiedCardinality &&
                          <div>
                            {isObjectProperty ?
                              <div>
                                {onClass ?
                                  <div
                                    data-test="onclass-container"
                                    style={{ marginTop: 6 }}>
                                    <Typography variant="caption" className={classes.instruction} style={{ marginBottom: 6 }}>On Class</Typography>
                                    <div>
                                      {onClass.startsWith(BN_NAMESPACE) ?
                                        <BlankNodeHandler
                                          key={onClass}
                                          id={onClass}
                                          graph={graph}
                                          context={context}
                                          useWorkflow={useWorkflow}
                                          currentRange={computedRange}
                                          isObjectProperty={isObjectProperty}
                                          handleNotAvailable={true}
                                          projectIRI={projectIRI}
                                          sourceResource={sourceResource}
                                          sourceComponent={sourceComponent}
                                          components={components}
                                          isReadOnly={isReadOnly}
                                          viewLang={viewLang}
                                          defaultLang={defaultLang} />
                                        :
                                        <CommonIriDisplay
                                          id={onClass}
                                          key={onClass}
                                          components={components}
                                          projectIRI={projectIRI}
                                          context={context}
                                          contextProperty="isDefinedBy"
                                          handleNotAvailable={true}
                                          property="onClass"
                                          isReadOnly={true}
                                          compact={true}
                                          graph={graph}
                                          source={id}
                                          wrap={false}
                                          viewLang={viewLang}
                                          defaultLang={defaultLang} />
                                      }
                                    </div>
                                  </div>
                                  :
                                  <div style={{ marginTop: 12 }}>
                                    {!isReadOnly &&
                                      <div>
                                        {isObjectProperty &&
                                          <Typography variant="caption" className={classes.instruction} style={{ marginBottom: 6 }}>Choose class</Typography>
                                        }
                                        <FindResourceDialog
                                          key={viewLang}
                                          useButton={true}
                                          permitExternal={true}
                                          components={components}
                                          exclude={[sourceResource]}
                                          hideButtonOnOpen={true}
                                          defaultLang={defaultLang}
                                          sourceComponent={sourceComponent}
                                          permittedExternalType="OwlClass"
                                          searchClass={[
                                            "http://www.w3.org/2002/07/owl#Class"
                                          ]}
                                          buttonTitle={isObjectProperty ? "Class" : "Add a Class"}
                                          title="Add Class"
                                          project={projectIRI}
                                          graph={graph}
                                          viewLang={viewLang}
                                          onAddResource={this.addOnClass} />
                                        <span className={classes.optionsButton} style={{ marginLeft: 24 }}>
                                          <SecondaryButton dataTest="complex-onclass" onClick={this.addValuesFrom("onClass")}>Class <span style={{ marginLeft: 12 }}><BuildIcon style={{ verticalAlign: 'middle' }} fontSize='small' /></span></SecondaryButton>
                                        </span>
                                      </div>
                                    }
                                  </div>
                                }
                              </div>
                              :
                              <div>
                                <div style={{ marginTop: 6 }}>
                                  {onDataRange ?
                                    <div>
                                      <Typography variant="caption" className={classes.instruction} style={{ marginBottom: 6 }}>On Data Range</Typography>
                                      {onDataRange.startsWith(BN_NAMESPACE) ?
                                        <BlankNodeHandler
                                          key={onDataRange}
                                          id={onDataRange}
                                          graph={graph}
                                          context={context}
                                          useWorkflow={useWorkflow}
                                          currentRange={computedRange}
                                          isObjectProperty={isObjectProperty}
                                          projectIRI={projectIRI}
                                          sourceResource={sourceResource}
                                          sourceComponent={sourceComponent}
                                          components={components}
                                          isReadOnly={isReadOnly}
                                          viewLang={viewLang}
                                          defaultLang={defaultLang} />
                                        :
                                        <CommonIriDisplay
                                          id={onDataRange}
                                          key={onDataRange}
                                          components={components}
                                          projectIRI={projectIRI}
                                          context={context}
                                          contextProperty="isDefinedBy"
                                          handleNotAvailable={true}
                                          property="onDataRange"
                                          isReadOnly={true}
                                          compact={true}
                                          graph={graph}
                                          source={id}
                                          wrap={false}
                                          viewLang={viewLang}
                                          defaultLang={defaultLang} />
                                      }
                                    </div>
                                    :
                                    <div>
                                      {!isReadOnly &&
                                        <div>
                                          <Typography variant="caption" className={classes.instruction} style={{ marginBottom: 6 }}>Choose from (optional)</Typography>
                                          <span className={classes.optionsButton}>
                                            <DatatypeDialog
                                              sourceComponent={sourceComponent}
                                              useButton={true}
                                              graph={graph}
                                              viewLang={viewLang}
                                              disabled={"onDataRange" in data && data.onRange !== undefined}
                                              onAddDatatype={this.addOnDataRange}
                                              title="Add Datatype"
                                              buttonTitle="Datatype"
                                              name="Datatype" />
                                          </span>
                                          <span className={classes.optionsButton}>
                                            <SecondaryButton dataTest="complex-data-range" onClick={() => this.addDatatype("onDataRange")}>Data Range</SecondaryButton>
                                          </span>
                                        </div>
                                      }
                                    </div>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        }
                      </div>
                      :
                      isReadOnly && constraintType ?
                        <div data-test="readonly-partial-constraint-context"></div>
                        :
                        <div data-test="constraint-context">
                          {
                            constraintType === "hasValue" ?
                              computedRange === "http://www.w3.org/2001/XMLSchema#boolean" ?
                                <GetBooleanValueDialog
                                  open={true}
                                  name="Enter Constraining Value"
                                  onAddLiteral={this.addNewTypedLiteral} />
                                :
                                <TypedLiteralDialog
                                  onAddLiteral={this.addNewTypedLiteral}
                                  title="Enter Constraining Value"
                                  open={true}
                                  datatypeFixed={computedRange && isDatatypeSupported ? true : false}
                                  datatype={computedRange && isDatatypeSupported ? computedRange : null}
                                  onExitLiteral={this.exitConstraint}
                                  placeholder="Enter value"
                                  name="Value" />
                              :
                              constraintType === "someValuesFromLiteralClass" || constraintType === "allValuesFromLiteralClass" ?
                                <DatatypeDialog
                                  onAddDatatype={constraintType === "someValuesFromLiteralClass" ?
                                    this.addSomeValuesFromClass
                                    :
                                    this.addAllaluesFromClass}
                                  onExitDatatype={this.exitConstraint}
                                  viewLang={viewLang}
                                  graph={graph}
                                  sourceComponent={sourceComponent}
                                  datatype={computedRange}
                                  disabled={computedRange !== undefined && isDatatypeSupported}
                                  title="Select Literal (Datatype) Class"
                                  open={true}
                                  name="Datatype" />
                                :
                                constraintType === "someValuesFrom" || constraintType === "allValuesFrom" ?
                                  <div style={{ marginTop: 12 }}>
                                    <FindResourceDialog
                                      key={viewLang}
                                      useButton={true}
                                      permitExternal={true}
                                      open={true}
                                      useWorkflow={useWorkflow}
                                      components={components}
                                      exclude={[sourceResource]}
                                      hideButtonOnOpen={true}
                                      defaultLang={defaultLang}
                                      permittedExternalType="OwlClass"
                                      sourceComponent={sourceComponent}
                                      searchClass={[
                                        "http://www.w3.org/2002/07/owl#Class"
                                      ]}
                                      buttonTitle="Add class"
                                      title="Add Class"
                                      project={projectIRI}
                                      graph={graph}
                                      viewLang={viewLang}
                                      onAddResource={constraintType === "someValuesFrom" ?
                                        this.addSomeValuesFromClass
                                        :
                                        this.addAllaluesFromClass} />
                                  </div>
                                  :
                                  hasValue !== undefined ?
                                    <div data-test="hasvalue-content">
                                      {computedRange === "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString" &&
                                        <Notification text="Language tagged strings are only partially supported." dataTest="partial-support" />
                                      }
                                      <DatatypeDisplay
                                        title={"hasValue" in data ? "" : "Constraining Value"}
                                        datatype={computedRange !== undefined ? computedRange : ""}
                                        defaultLang={defaultLang}
                                        viewLang={viewLang}
                                        projectLang={projectLang}
                                        useWorkflow={useWorkflow}
                                        property="hasValue"
                                        values={hasValue !== undefined ? [hasValue] : []}
                                        associatedProjectGraph={graph}
                                        context={context}
                                        projectIRI={projectIRI}
                                        graph={graph}
                                        sourceResource={sourceResource}
                                        onUpdate={() => this.onUpdate("hasValue")}
                                        sourceComponent={sourceComponent}
                                        isReadOnly={isReadOnly || isObjectProperty || computedRange === "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString"} />
                                    </div>
                                    :
                                    hasSelf ?
                                      <LiteralConfirmationField
                                        graph={graph}
                                        dataTest="has-self"
                                        source={id}
                                        property="hasSelf"
                                        margin="dense"
                                        text="TRUE"
                                        datatype="http://www.w3.org/2001/XMLSchema#boolean"
                                        canBeDeleted={false}
                                        onUpdate={this.onUpdate("hasSelf")}
                                        isReadOnly={true}
                                      />
                                      :
                                      !from && isReadOnly ?
                                        <div data-test="readonly-partial-constraint-context-no-content"></div>
                                        :
                                        !from ?
                                          chooseCardinality ?
                                            <div>
                                              <span className={classes.optionsButton}>
                                                <SecondaryButton dataTest="exact" onClick={() => this.addCardinalityProp("cardinality")}>Exact</SecondaryButton>
                                              </span>
                                              <span className={classes.optionsButton}>
                                                <SecondaryButton dataTest="minimum" onClick={() => this.addCardinalityProp("minCardinality")}>Minimum</SecondaryButton>
                                              </span>
                                              <span className={classes.optionsButton}>
                                                <SecondaryButton dataTest="maximum" onClick={() => this.addCardinalityProp("maxCardinality")}>Maximum</SecondaryButton>
                                              </span>
                                            </div>
                                            :
                                            chooseQualifiedCardinality ?
                                              <div>
                                                <span className={classes.optionsButton}>
                                                  <SecondaryButton dataTest="exact" onClick={() => this.addCardinalityProp("qualifiedCardinality")}>Exact</SecondaryButton>
                                                </span>
                                                <span className={classes.optionsButton}>
                                                  <SecondaryButton dataTest="minimum" onClick={() => this.addCardinalityProp("minQualifiedCardinality")}>Minimum</SecondaryButton>
                                                </span>
                                                <span className={classes.optionsButton}>
                                                  <SecondaryButton dataTest="maximum" onClick={() => this.addCardinalityProp("maxQualifiedCardinality")}>Maximum</SecondaryButton>
                                                </span>
                                              </div>
                                              :
                                              <div>
                                                {isObjectProperty ?
                                                  <span>
                                                    <span className={classes.optionsButton}>
                                                      <AdvancedButtons
                                                        property="someValuesFrom"
                                                        dataTest="someValuesFrom"
                                                        title="Some"
                                                        onAddRestriction={this.addNewRestriction}
                                                        onAddExpression={this.addValuesFrom}
                                                        onAddSimple={(property) => () => this.setConstraintType(property)} />
                                                    </span>
                                                    <span className={classes.optionsButton}>
                                                      <AdvancedButtons
                                                        property="allValuesFrom"
                                                        dataTest="allValuesFrom"
                                                        title="All"
                                                        onAddRestriction={this.addNewRestriction}
                                                        onAddExpression={this.addValuesFrom}
                                                        onAddSimple={(property) => () => this.setConstraintType(property)} />
                                                    </span>
                                                    <span className={classes.optionsButton}>
                                                      <FindResourceDialog
                                                        key={viewLang}
                                                        useButton={true}
                                                        permitExternal={false}
                                                        components={components}
                                                        exclude={[sourceResource]}
                                                        hideButtonOnOpen={true}
                                                        defaultLang={defaultLang}
                                                        sourceComponent={sourceComponent}
                                                        searchClass={[
                                                          "http://www.w3.org/2002/07/owl#NamedIndividual"
                                                        ]}
                                                        buttonTitle="Has Value"
                                                        title="Find Named Individual"
                                                        project={projectIRI}
                                                        graph={graph}
                                                        viewLang={viewLang}
                                                        onAddResource={this.addHasValueIRI} />
                                                    </span>
                                                    <span className={classes.optionsButton}>
                                                      <SecondaryButton dataTest="hs" onClick={this.addHasSelf}>Has Self</SecondaryButton>
                                                    </span>
                                                  </span>
                                                  :
                                                  <span>
                                                    <span className={classes.optionsButton}>
                                                      <SecondaryButton dataTest="svfl" onClick={() => this.setConstraintType("someValuesFromLiteralClass")}>Some Literal Class</SecondaryButton>
                                                    </span>
                                                    <span className={classes.optionsButton}>
                                                      <SecondaryButton dataTest="svf" onClick={() => this.addDatatype("someValuesFrom")}>Some</SecondaryButton>
                                                    </span>
                                                    <span className={classes.optionsButton}>
                                                      <SecondaryButton dataTest="avfl" onClick={() => this.setConstraintType("allValuesFromLiteralClass")}>All Literal Class</SecondaryButton>
                                                    </span>
                                                    <span className={classes.optionsButton}>
                                                      <SecondaryButton dataTest="avf" onClick={() => this.addDatatype("allValuesFrom")}>All</SecondaryButton>
                                                    </span>
                                                    {![
                                                      "http://www.w3.org/2001/XMLSchema#dateTime",
                                                      "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString"
                                                    ].includes(computedRange) &&
                                                      <span className={classes.optionsButton}>
                                                        <SecondaryButton dataTest="hv" onClick={() => this.setConstraintType("hasValue")}>Has Value</SecondaryButton>
                                                      </span>
                                                    }
                                                  </span>
                                                }
                                                {!isTransitiveProperty &&
                                                  <React.Fragment>
                                                    <span className={classes.optionsButton}>
                                                      <SecondaryButton dataTest="cardinality" onClick={this.onChooseCardinality}>Cardinality</SecondaryButton>
                                                    </span>
                                                    <span className={classes.optionsButton}>
                                                      <SecondaryButton dataTest="qualified" onClick={this.onChooseQualifiedCardinality}>Qualified Cardinality</SecondaryButton>
                                                    </span>
                                                  </React.Fragment>
                                                }
                                              </div>
                                          :
                                          from.startsWith(BN_NAMESPACE) ?
                                            <BlankNodeHandler
                                              key={from}
                                              id={from}
                                              graph={graph}
                                              useWorkflow={useWorkflow}
                                              context={context}
                                              currentRange={computedRange}
                                              handleNotAvailable={true}
                                              projectIRI={projectIRI}
                                              sourceResource={sourceResource}
                                              sourceComponent={sourceComponent}
                                              components={components}
                                              isReadOnly={isReadOnly}
                                              viewLang={viewLang}
                                              defaultLang={defaultLang} />
                                            :
                                            <CommonIriDisplay
                                              id={from}
                                              key={from}
                                              components={components}
                                              projectIRI={projectIRI}
                                              context={context}
                                              contextProperty="isDefinedBy"
                                              handleNotAvailable={true}
                                              sourceComponent={sourceComponent}
                                              isReadOnly={true}
                                              compact={true}
                                              graph={graph}
                                              source={id}
                                              wrap={false}
                                              viewLang={viewLang}
                                              defaultLang={defaultLang} />
                          }
                        </div>
                  }
                </div>
              }
            </div>
          </div>
        )
        }
      </ProjectConsumer>
    );
  }
}

RestrictionHandler.propTypes = {
  id: PropTypes.string,
  top: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  data: PropTypes.object,
  defaultLang: PropTypes.string,
  viewLang: PropTypes.string,
  sourceComponent: PropTypes.string.isRequired,
  components: PropTypes.array,
  graph: PropTypes.string,
  projectIRI: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool
};

export default withStyles(styles)(withGS(RestrictionHandler));